import { useEffect, useMemo, useRef } from "react";
import { ChevronDown, Mail, MapPin, Phone, RefreshCw, Settings, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { IN_FORCE_STATUSES } from "@shared/schema";
import {
  INSURED_SYNC_NONE_ELIGIBLE_TOOLTIP,
  isPolicySyncEligible,
} from "@shared/policy-sync-eligibility";
import type { Insured, Policy } from "@shared/schema";
import { formatLastUpdated, formatPhoneDisplay } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import {
  useInsuredPoliciesSyncStatus,
  useSyncInsuredPolicies,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { InsuredStatusBadges, hasInsuredStatus } from "./InsuredStatusBadges";
import { EditInsuredDialog } from "./EditInsuredDialog";

interface Props {
  insured: Insured;
  policies: Policy[];
  /**
   * CSR-1970 — did the policy list actually LOAD, as opposed to arriving empty?
   * The page collapses a failed `/insureds/:id/policies` request to `[]`, so
   * `policies.length === 0` alone cannot tell "this insured has no policies"
   * from "we could not find out". The delete confirmation must fail closed on
   * the second, or it would promise no policies are affected while DC goes on to
   * delete live ones the user never saw. Optional so every other caller of this
   * component is unaffected; only the delete path reads it.
   */
  policiesLoaded?: boolean;
  // CSR-2327 — the AOR experience flag, read once by the insured page.
  showAor?: boolean;
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold">{value || "—"}</p>
    </div>
  );
}

export function InsuredSidebar({
  insured,
  policies,
  policiesLoaded,
  showAor = false,
}: Props) {
  const sectionClass = "px-6 py-5";
  const { toast } = useToast();
  const customerName = insured.name || insured.commercialName || "";
  // CSR-1129: the sync is keyed by the insured *id* (unique, URL-safe), not
  // the name (non-unique). The page already loads policies by this id via
  // `useInsuredPolicies(insured.id)`, so Refresh now hits the same source of
  // truth. `customerName` is retained purely for the display/disabled copy.
  const syncMutation = useSyncInsuredPolicies(insured.id);

  // CSR-2169 — the shared eligibility rule the server fan-out applies too:
  // supported carrier (GEICO/Progressive) AND the most recent term of its
  // policy number. Zero eligible policies means the Refresh would enqueue
  // nothing, so the button disables with the approved tooltip instead.
  const hasSyncablePolicies = useMemo(
    () => policies.some((p) => p.externalId && isPolicySyncEligible(p, policies)),
    [policies],
  );

  // CSR-1090: drive the spinner from the server-side poll status rather
  // than the (now near-instant) HTTP mutation. The browser polls a
  // batched status endpoint every 5s while any policy is still syncing.
  const watchedPolicyIds = useMemo(
    () => policies.filter((p) => p.externalId).map((p) => p.id),
    [policies],
  );

  // CSR-1881 — the producer(s) who sold this account's policies. Passed to the
  // edit dialog so a former/terminated producer stays selectable on their own
  // historical account (the Flint roster only carries current producers).
  const policyProducers = useMemo(
    () => policies.map((p) => p.producer).filter((p): p is string => !!p),
    [policies],
  );
  const { data: syncStatus } = useInsuredPoliciesSyncStatus(
    insured.id,
    watchedPolicyIds,
    watchedPolicyIds.length > 0,
  );
  const isSyncing = (syncStatus?.policies ?? []).some((p) => p.syncing);

  // CSR-1090 cycle 2: stamp the outcome timestamps observed at the
  // start of *this* sync run so we only toast on freshly-recorded
  // failures. Without this, a page load that happens to coincide with
  // a stale failure activity row would surface a misleading toast.
  const outcomeAtAtSyncStartRef = useRef<Map<string, string | null>>(new Map());
  // CSR-1090 cycle 2: gate the completion toast so background syncs
  // initiated from another tab/session don't fire unsolicited toasts
  // on this tab. The mutation's onSuccess sets this; the transition
  // effect clears it.
  const userInitiatedRef = useRef(false);
  const wasSyncingRef = useRef(false);

  useEffect(() => {
    // Snapshot the outcome timestamps the first tick we observe any
    // policy in flight, so the transition handler can tell a fresh
    // failure (recorded since we started watching) from a stale one.
    if (isSyncing && !wasSyncingRef.current) {
      const snapshot = new Map<string, string | null>();
      for (const p of syncStatus?.policies ?? []) {
        snapshot.set(p.policyId, p.lastSyncOutcomeAt ?? null);
      }
      outcomeAtAtSyncStartRef.current = snapshot;
    }
    if (wasSyncingRef.current && !isSyncing) {
      // Detect failures recorded during this sync run.
      // CSR-1703 (R-001): `not_found` is a distinct terminal outcome (e.g.
      // GEICO could not locate the policy) but is still a failure from the
      // user's point of view — treat it the same as `"failed"` here so the
      // bulk-sync toast doesn't falsely report success.
      const freshFailures = (syncStatus?.policies ?? []).filter((p) => {
        if (p.lastSyncOutcome !== "failed" && p.lastSyncOutcome !== "not_found") return false;
        const baseline = outcomeAtAtSyncStartRef.current.get(p.policyId) ?? null;
        return p.lastSyncOutcomeAt !== baseline;
      });
      if (userInitiatedRef.current) {
        if (freshFailures.length > 0) {
          const first = freshFailures[0];
          const errMessage = first.lastSyncError ?? "Carrier sync did not complete.";
          const description =
            freshFailures.length === 1
              ? errMessage
              : `${freshFailures.length} policies failed to sync — ${errMessage}`;
          toast({
            title: "Some policies failed to sync",
            description,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Policies synced",
            description: "Fresh data has been synced from the carriers via Data Center.",
          });
        }
        userInitiatedRef.current = false;
      }
      outcomeAtAtSyncStartRef.current = new Map();
    }
    wasSyncingRef.current = isSyncing;
  }, [isSyncing, syncStatus, toast]);

  // CSR-1079 — the "Updated" line shows when this insured's data was last
  // scraped from the carrier portal: DC's `last_sync`, mirrored into
  // `carrierScrapedAt`. Unlike `syncedAt` (AMS↔DC poll contact, which churns
  // every few minutes) this advances only on a real carrier extraction.
  // Safeguard: when no carrier-scrape time is available yet — the forward-fill
  // window right after deploy, or NowCerts-only insureds — fall back to
  // `syncedAt` (the pre-CSR-1079 behaviour) so the line never regresses to
  // "Not yet synced". Each candidate uses the most recent value across in-force
  // policies, then the insured-level value.
  const lastUpdatedAt = useMemo<Date | null>(() => {
    const mostRecentInForce = (
      get: (p: Policy) => Date | string | null | undefined,
    ): number | null => {
      const times = policies
        .filter((p) =>
          IN_FORCE_STATUSES.includes(
            (p.status?.toLowerCase() ?? "") as (typeof IN_FORCE_STATUSES)[number],
          ),
        )
        .map(get)
        .filter((t): t is NonNullable<typeof t> => t != null)
        .map((t) => new Date(t).getTime())
        .filter((ms) => !Number.isNaN(ms));
      return times.length > 0 ? Math.max(...times) : null;
    };
    const toMs = (t: Date | string | null | undefined): number | null => {
      if (t == null) return null;
      const ms = new Date(t).getTime();
      return Number.isNaN(ms) ? null : ms;
    };
    const carrierScrape =
      mostRecentInForce((p) => p.carrierScrapedAt) ?? toMs(insured.carrierScrapedAt);
    const amsSync = mostRecentInForce((p) => p.syncedAt) ?? toMs(insured.syncedAt);
    const chosen = carrierScrape ?? amsSync;
    return chosen != null ? new Date(chosen) : null;
  }, [policies, insured.carrierScrapedAt, insured.syncedAt]);

  // CSR-1703 (R-001): mirror PolicyDetailHeader's single-policy suppression
  // at the insured level. `lastUpdatedAt` above is derived from
  // `carrierScrapedAt`, which DC advances even on a `not_found` scrape
  // (queue throttling) — so the raw timestamp alone can look "fresh" even
  // though the carrier never confirmed the data. We can't tell from the
  // chosen timestamp alone *which* policy contributed it, and a failed/
  // not_found outcome on ANY in-force, syncable policy means that policy's
  // data on this page may be stale — so we suppress the fresh "Updated …"
  // impression whenever any in-force policy's latest known sync outcome is
  // a failure, rather than trying to attribute the timestamp to one policy.
  const hasFailedInForceSyncOutcome = useMemo(() => {
    const inForcePolicyIds = new Set(
      policies
        .filter((p) =>
          IN_FORCE_STATUSES.includes(
            (p.status?.toLowerCase() ?? "") as (typeof IN_FORCE_STATUSES)[number],
          ),
        )
        .map((p) => p.id),
    );
    return (syncStatus?.policies ?? []).some(
      (p) =>
        inForcePolicyIds.has(p.policyId) &&
        (p.lastSyncOutcome === "failed" || p.lastSyncOutcome === "not_found"),
    );
  }, [policies, syncStatus]);

  // CSR-1090: `isSyncing` (from the server-side poll status) drives the
  // spinner. `syncMutation.isPending` is used as the disabled gate only
  // during the brief window the POST is in flight — it flips back to
  // false near-instantly, and `isSyncing` takes over for the long wait.
  const buttonBusy = syncMutation.isPending || isSyncing;
  const canSync = !!customerName && hasSyncablePolicies && !buttonBusy;
  const disabledReason = !customerName
    ? "Insured is missing a name to sync"
    : !hasSyncablePolicies
      ? INSURED_SYNC_NONE_ELIGIBLE_TOOLTIP
      : null;

  const handleRefresh = () => {
    if (!canSync) return;
    syncMutation.mutate(undefined, {
      onSuccess: (data) => {
        // CSR-1160 (A-001): a terminal `removed` outcome is fully handled by
        // the hook (toast + query invalidation + redirect to /insureds). Do
        // not stamp the user-initiated flag or show the "Sync started" toast —
        // there is nothing to poll and this page is being navigated away from.
        if (data?.status === "removed") return;
        // CSR-1090: the POST is fire-and-forget — the real outcome
        // surfaces when the polling hook detects all policies done.
        // CSR-1090 cycle 2: stamp `userInitiatedRef` so the completion
        // toast only fires on this tab when *this* user clicked
        // refresh. Background syncs initiated from another tab/session
        // are silent here.
        userInitiatedRef.current = true;
        toast({
          title: "Sync started",
          description: "Fetching latest policy data from the carriers via Data Center.",
        });
      },
      onError: (err: Error) =>
        toast({ title: "Sync failed", description: err.message, variant: "destructive" }),
    });
  };

  const refreshButton = (
    <button
      type="button"
      onClick={handleRefresh}
      disabled={!canSync}
      aria-label={isSyncing ? "Syncing policies" : "Refresh policies"}
      className={cn(
        "shrink-0 inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground transition-colors",
        canSync ? "hover:text-foreground cursor-pointer" : "cursor-not-allowed opacity-60",
        // CSR-2169 — a disabled button swallows pointer/focus events; when the
        // reason tooltip is up (disabledReason), the focusable wrapper below is
        // the trigger, so events must pass through the button to reach it.
        disabledReason && "pointer-events-none",
      )}
      data-testid="button-refresh-insured-sidebar"
    >
      <RefreshCw className={cn("w-3.5 h-3.5", isSyncing && "animate-spin")} />
    </button>
  );

  return (
    <aside
      className="w-full md:w-72 shrink-0 border-b md:border-b-0 md:border-r border-border bg-card md:sticky md:top-0 md:self-start md:h-[calc(100vh-3.5rem)] md:overflow-y-auto"
      data-testid="insured-sidebar"
    >
      <div className="divide-y divide-border">
        {/* Header */}
        <div className={sectionClass}>
          <h1
            className="text-base font-bold uppercase tracking-tight leading-tight"
            data-testid="insured-sidebar-name"
          >
            {insured.name}
          </h1>
          {insured.dbaName && (
            <p
              className="text-xs text-muted-foreground mt-0.5"
              data-testid="insured-sidebar-dba"
            >
              DBA: {insured.dbaName}
            </p>
          )}
          {insured.dotNumber && (
            <p className="text-xs text-muted-foreground mt-1">DOT: {insured.dotNumber}</p>
          )}
          <div className="mt-1 flex items-center gap-1.5">
            <p
              className="text-xs text-muted-foreground"
              data-testid="text-insured-last-updated"
            >
              {isSyncing
                ? "Fetching latest policy data..."
                : hasFailedInForceSyncOutcome
                  ? "Last sync did not complete — see policy for details"
                  : lastUpdatedAt
                    ? `Updated ${formatLastUpdated(lastUpdatedAt)}`
                    : "Not yet synced"}
            </p>
            {disabledReason ? (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className="inline-flex cursor-not-allowed"
                      data-testid="refresh-insured-tooltip-target"
                    >
                      {refreshButton}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{disabledReason}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              refreshButton
            )}
          </div>
        </div>

        {/* Contact */}
        <div className={`${sectionClass} space-y-2`}>
          {insured.companyOfficer && (
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="truncate font-semibold" title={insured.companyOfficer}>
                {insured.companyOfficer}
              </span>
            </div>
          )}
          {insured.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="truncate" title={insured.email}>
                {insured.email}
              </span>
            </div>
          )}
          {insured.telephone && (
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
              <span>{formatPhoneDisplay(insured.telephone)}</span>
            </div>
          )}
          {/* CSR-1334 — collapsible mailing address, collapsed by default */}
          {insured.mailingStreet && (
            <Collapsible>
              <CollapsibleTrigger
                className="group flex w-full items-center gap-2 text-sm cursor-pointer"
                data-testid="sidebar-mailing-address-trigger"
              >
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                <span>Mailing Address</span>
                <ChevronDown className="ml-auto w-4 h-4 text-muted-foreground shrink-0 transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent
                className="pl-6 pt-1 text-sm"
                data-testid="sidebar-mailing-address-content"
              >
                <p>{insured.mailingStreet}</p>
                <p>
                  {[
                    [insured.mailingCity, insured.mailingState].filter(Boolean).join(", "),
                    insured.mailingZip,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  {insured.mailingCountry && insured.mailingCountry !== "US" && (
                    <>, {insured.mailingCountry}</>
                  )}
                </p>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {/* Status */}
        {hasInsuredStatus(policies, showAor) && (
          <div className={`${sectionClass} space-y-1.5`}>
            <p className="text-xs text-muted-foreground">Status</p>
            <InsuredStatusBadges policies={policies} showAor={showAor} />
          </div>
        )}

        {/* Business type */}
        <div className={sectionClass}>
          <Field label="Business Type" value={insured.businessOrg} />
        </div>

        {/* Radius */}
        <div className={sectionClass}>
          <Field label="Radius" value={insured.radius} />
        </div>

        {/* Account team */}
        <div className={`${sectionClass} space-y-3`}>
          <p className="text-xs text-muted-foreground">Account Team</p>
          <div>
            <p className="text-xs text-muted-foreground">Sales Agent</p>
            <p className="text-sm font-semibold" data-testid="sidebar-sales-agent">{insured.salesAgent || "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Retention Agent</p>
            <p className="text-sm font-semibold" data-testid="sidebar-retention-agent">{insured.retentionAgent || "—"}</p>
          </div>
        </div>

        {/* Actions */}
        <div className={sectionClass}>
          {/* CSR-1970 — Delete is INSIDE this dialog, not beside it. Same shape as
              a policy: the destructive action lives behind the edit surface you
              already had to open, rather than one click from the profile. */}
          <EditInsuredDialog
            insured={insured}
            policyProducers={policyProducers}
            policies={policies}
            policiesLoaded={policiesLoaded}
          >
            <Button
              variant="outline"
              className="w-full justify-start gap-2 cursor-pointer"
              data-testid="sidebar-edit"
            >
              <Settings className="w-4 h-4" /> Edit
            </Button>
          </EditInsuredDialog>
        </div>
      </div>
    </aside>
  );
}
